import React, { useCallback, useEffect, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { toast } from "sonner";
import {
  CheckCircle2, Ban, Shield, Trash2, Award, Users, Building2, FileText,
  TrendingUp, MessageSquare, Handshake, DollarSign, Landmark, Sparkles, Search, Eye, EyeOff, Eraser,
  Activity, Radio, Zap,
} from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

const bandColor = (band) =>
  band === "Élite" ? "bg-gradient-to-r from-gold to-amber-500 text-petroleum" :
  band === "Confiable" ? "bg-emerald-100 text-emerald-700" :
  band === "En crecimiento" ? "bg-blue-100 text-blue-700" :
  "bg-slate-100 text-slate-600";

export default function AdminPanel() {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [creators, setCreators] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [projects, setProjects] = useState([]);
  const [query, setQuery] = useState("");
  const [includeTest, setIncludeTest] = useState(false);
  const [includeDrafts, setIncludeDrafts] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [health, setHealth] = useState(null);

  const load = useCallback(async () => {
    const [{ data: s }, { data: c }, { data: sp }, { data: pr }, { data: h }] = await Promise.all([
      api.get("/api/admin/stats"),
      api.get("/api/admin/creators", { params: { include_test: includeTest } }),
      api.get("/api/admin/sponsors", { params: { include_test: includeTest } }),
      api.get("/api/admin/projects", { params: { include_test: includeTest, include_drafts: includeDrafts } }),
      api.get("/api/admin/system-health").catch(() => ({ data: null })),
    ]);
    setStats(s);
    setCreators(c);
    setSponsors(sp);
    setProjects(pr);
    setHealth(h);
  }, [includeTest, includeDrafts]);
  useEffect(() => { load(); }, [load]);

  const verify = async (id) => {
    await api.post(`/api/admin/users/${id}/verify`);
    toast.success(t("admin.verified_toast"));
    load();
  };
  const suspend = async (id, current) => {
    const path = current ? "unsuspend" : "suspend";
    await api.post(`/api/admin/users/${id}/${path}`);
    toast.success(current ? t("admin.user_reactivated") : t("admin.user_suspended"));
    load();
  };
  const remove = async (id, email) => {
    try {
      await api.delete(`/api/admin/users/${id}`);
      toast.success(t("admin.user_deleted", { email }));
      load();
    } catch (e) {
      toast.error(`${t("admin.delete_failed")}: ${e.response?.data?.detail || "error"}`);
    }
  };

  const cleanupTest = async () => {
    setCleaning(true);
    try {
      const { data } = await api.post("/api/admin/cleanup-test-users");
      toast.success(t("admin.cleanup_ok", { count: data.count }));
      await load();
    } catch (e) {
      toast.error(`${t("admin.cleanup_failed")}: ${e.response?.data?.detail || "error"}`);
    } finally {
      setCleaning(false);
    }
  };

  const cleanupOrphanDrafts = async () => {
    setCleaning(true);
    try {
      const { data } = await api.post("/api/admin/cleanup-orphan-drafts");
      toast.success(t("admin.cleanup_drafts_ok", { count: data.count }));
      await load();
    } catch (e) {
      toast.error(`${t("admin.cleanup_failed")}: ${e.response?.data?.detail || "error"}`);
    } finally {
      setCleaning(false);
    }
  };

  const filterQ = (u) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q);
  };

  return (
    <DashboardLayout title={t("admin.title")} subtitle={t("admin.subtitle")}>
      <div className="flex flex-wrap gap-3 mb-6">
        <a href="/dashboard/admin/verificaciones" className="text-sm text-petroleum hover:text-gold inline-flex items-center gap-2 border border-slate-200 rounded-full px-4 py-2" data-testid="link-admin-verifications">
          {t("admin.verifications_link")}
        </a>
        <a href="/dashboard/convocatorias" className="text-sm text-petroleum hover:text-gold inline-flex items-center gap-2 border border-slate-200 rounded-full px-4 py-2" data-testid="link-admin-funds">
          {t("admin.funds_link")}
        </a>
        <a href="/vitrina" target="_blank" rel="noreferrer" className="text-sm text-petroleum hover:text-gold inline-flex items-center gap-2 border border-slate-200 rounded-full px-4 py-2" data-testid="link-admin-vitrina">
          {t("admin.vitrina_link")}
        </a>
      </div>

      {stats && (
        <>
          {typeof stats.published_today === "number" && (
            <div
              data-testid="kpi-published-today"
              className="mb-6 rounded-2xl border border-gold/30 bg-gradient-to-r from-gold/10 via-gold/5 to-transparent p-5 flex items-center gap-4 flex-wrap"
            >
              <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-gold text-petroleum shrink-0">
                <TrendingUp size={22} />
              </div>
              <div className="flex-1 min-w-[220px]">
                <p className="text-[10px] uppercase tracking-widest text-gold font-semibold">{t("admin.published_today_label")}</p>
                <p className="font-display text-3xl text-petroleum tabular-nums mt-0.5" data-testid="published-today-value">
                  {stats.published_today}
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  <span data-testid="published-today-projects" className="font-semibold text-petroleum">{stats.projects_today}</span> {t("admin.published_today_projects", { count: stats.projects_today, plural: stats.projects_today === 1 ? "" : "s" })} · <span data-testid="published-today-briefs" className="font-semibold text-petroleum">{stats.briefs_today}</span> {t("admin.published_today_briefs", { count: stats.briefs_today, plural: stats.briefs_today === 1 ? "" : "s" })}
                </p>
              </div>
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-widest text-slate-500">{t("admin.window")}</p>
                <p className="text-xs text-slate-700">{t("admin.window_desc")}</p>
              </div>
            </div>
          )}

          <p className="eyebrow text-gold mb-3">{t("admin.global_stats")}</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="admin-stats-grid">
            <Kpi l={t("admin.kpi_users")} v={stats.users} icon={Users} tone="petroleum" />
            <Kpi l={t("admin.kpi_creators")} v={stats.creators} icon={FileText} />
            <Kpi l={t("admin.kpi_sponsors")} v={stats.sponsors} icon={Building2} />
            <Kpi l={t("admin.kpi_admins")} v={stats.admins} icon={Shield} tone="petroleum" />
            <Kpi l={t("admin.kpi_published_projects")} v={stats.published_projects} icon={FileText} />
            <Kpi l={t("admin.kpi_active_briefs")} v={stats.active_briefs} icon={FileText} />
            <Kpi l={t("admin.kpi_rooms")} v={stats.rooms} icon={Handshake} />
            <Kpi l={t("admin.kpi_signed")} v={stats.signed_agreements} icon={Handshake} tone="emerald" />
            <Kpi l={t("admin.kpi_contacts")} v={stats.contacts} icon={MessageSquare} />
            <Kpi l={t("admin.kpi_proposals")} v={stats.proposals} icon={MessageSquare} />
            <Kpi l={t("admin.kpi_certs")} v={stats.certificates} icon={Award} tone="gold" />
            <Kpi l={t("admin.kpi_vitrina")} v={stats.highlights} icon={Sparkles} tone="gold" />
          </div>

          {Object.keys(stats.paid_by_currency || {}).length > 0 && (
            <div className="kpi-card p-5 mb-6">
              <p className="eyebrow text-gold mb-2">{t("admin.paid_by_currency")}</p>
              <div className="flex flex-wrap gap-3">
                {Object.entries(stats.paid_by_currency).map(([cur, amt]) => (
                  <div key={cur} className="border border-slate-200 rounded-xl px-4 py-2">
                    <p className="text-[10px] uppercase tracking-widest text-slate-500">{cur}</p>
                    <p className="font-display text-xl text-petroleum">{Number(amt).toLocaleString()}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {health && <HealthCard t={t} health={health} />}
        </>
      )}

      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder={t("admin.search_placeholder")} value={query} onChange={(e) => setQuery(e.target.value)} data-testid="input-admin-search" className="pl-9 w-72" />
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setIncludeDrafts((v) => !v)}
            data-testid="button-toggle-drafts"
            className="text-xs h-8"
          >
            {includeDrafts ? <><EyeOff size={12} className="mr-1" /> {t("admin.toggle_drafts_hide")}</> : <><Eye size={12} className="mr-1" /> {t("admin.toggle_drafts_show")}</>}
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                size="sm"
                variant="outline"
                data-testid="button-cleanup-orphan-drafts"
                disabled={cleaning || !(stats?.orphan_drafts > 0)}
                className="text-xs h-8 text-red-600 border-red-200 hover:bg-red-50"
              >
                <Eraser size={12} className="mr-1" /> {cleaning ? t("admin.cleaning") : `${t("admin.cleanup_drafts")}${stats?.orphan_drafts ? ` (${stats.orphan_drafts})` : ""}`}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t("admin.cleanup_drafts_title")}</AlertDialogTitle>
                <AlertDialogDescription>
                  <Trans
                    i18nKey="admin.cleanup_drafts_desc"
                    values={{ count: stats?.orphan_drafts || 0 }}
                    components={{ b: <b />, code: <code /> }}
                  />
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                <AlertDialogAction onClick={cleanupOrphanDrafts} data-testid="button-confirm-cleanup-orphan-drafts" className="bg-red-600 hover:bg-red-700">
                  {t("admin.yes_delete")}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setIncludeTest((v) => !v)}
            data-testid="button-toggle-test-users"
            className="text-xs h-8"
          >
            {includeTest ? <><EyeOff size={12} className="mr-1" /> {t("admin.toggle_test_hide")}</> : <><Eye size={12} className="mr-1" /> {t("admin.toggle_test_show")}</>}
            {stats?.test_users != null && (
              <span className="ml-2 text-[10px] rounded-full bg-slate-100 text-slate-600 px-2 py-0.5 tabular-nums">
                {stats.test_users}
              </span>
            )}
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                size="sm"
                variant="outline"
                data-testid="button-cleanup-test-users"
                disabled={cleaning || !(stats?.test_users > 0)}
                className="text-xs h-8 text-red-600 border-red-200 hover:bg-red-50"
              >
                <Eraser size={12} className="mr-1" /> {cleaning ? t("admin.cleaning") : `${t("admin.cleanup_test")}${stats?.test_users ? ` (${stats.test_users})` : ""}`}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t("admin.cleanup_test_title")}</AlertDialogTitle>
                <AlertDialogDescription>
                  <Trans
                    i18nKey="admin.cleanup_test_desc"
                    values={{ count: stats?.test_users || 0 }}
                    components={{ b: <b />, code: <code /> }}
                  />
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                <AlertDialogAction onClick={cleanupTest} data-testid="button-confirm-cleanup" className="bg-red-600 hover:bg-red-700">
                  {t("admin.yes_cleanup")}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <Tabs defaultValue="creators">
        <TabsList className="bg-slate-100 rounded-full mb-6">
          <TabsTrigger value="creators" data-testid="tab-creators">
            <FileText size={12} className="mr-1" /> {t("admin.tab_creators")} ({creators.length})
          </TabsTrigger>
          <TabsTrigger value="sponsors" data-testid="tab-sponsors">
            <Building2 size={12} className="mr-1" /> {t("admin.tab_sponsors")} ({sponsors.length})
          </TabsTrigger>
          <TabsTrigger value="projects" data-testid="tab-projects">
            {t("admin.tab_projects")} ({projects.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="creators">
          <UsersTable
            t={t}
            role="creator"
            rows={creators.filter(filterQ)}
            columns={[
              { key: "projects_total", label: t("admin.col_projects") },
              { key: "published_total", label: t("admin.col_published") },
              { key: "rooms_total", label: t("admin.col_rooms") },
              { key: "agreements_signed", label: t("admin.col_agreements") },
            ]}
            onVerify={verify}
            onSuspend={suspend}
            onDelete={remove}
          />
        </TabsContent>

        <TabsContent value="sponsors">
          <UsersTable
            t={t}
            role="sponsor"
            rows={sponsors.filter(filterQ)}
            columns={[
              { key: "briefs_total", label: t("admin.col_briefs") },
              { key: "active_briefs", label: t("admin.col_active_briefs") },
              { key: "rooms_total", label: t("admin.col_rooms") },
              { key: "agreements_signed", label: t("admin.col_agreements") },
              { key: "certificates_issued", label: t("admin.col_certs") },
            ]}
            onVerify={verify}
            onSuspend={suspend}
            onDelete={remove}
          />
        </TabsContent>

        <TabsContent value="projects">
          <div className="kpi-card overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-widest text-slate-500">{t("admin.col_title")}</th>
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-widest text-slate-500">{t("admin.col_format")}</th>
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-widest text-slate-500">{t("admin.col_status")}</th>
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-widest text-slate-500">{t("admin.col_views")}</th>
                </tr>
              </thead>
              <tbody>
                {projects.map((p) => (
                  <tr key={p.id} className="border-t border-slate-100">
                    <td className="px-4 py-3 text-sm text-petroleum">{p.title}</td>
                    <td className="px-4 py-3 text-sm capitalize">{p.format}</td>
                    <td className="px-4 py-3">{p.published ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">{t("admin.status_published")}</Badge> : <Badge variant="secondary">{t("admin.status_draft")}</Badge>}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{p.views || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </DashboardLayout>
  );
}

function HealthCard({ t, health }) {
  const push = health.sse_mode === "push";
  return (
    <div className="kpi-card p-5 mb-8" data-testid="admin-system-health">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="eyebrow text-gold mb-1">{t("admin.health_title")}</p>
          <p className="text-xs text-slate-500">{t("admin.health_subtitle")}</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold ${push ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`} data-testid="health-sse-mode">
            {push ? <Zap size={12} /> : <Radio size={12} />}
            {push ? t("admin.health_push") : t("admin.health_polling")}
          </div>
          {health.replica_set ? (
            <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px]">
              {t("admin.health_replica", { name: health.replica_set_name })}
            </Badge>
          ) : (
            <Badge variant="secondary" className="text-[10px]">{t("admin.health_standalone")}</Badge>
          )}
          {health.mongo_version && (
            <Badge variant="outline" className="text-[10px] border-slate-200 text-slate-600">
              MongoDB {health.mongo_version}
            </Badge>
          )}
        </div>
      </div>
      {!push && health.change_stream_error && (
        <p className="text-[10px] text-slate-400 mt-3">
          <Trans
            i18nKey="admin.health_note"
            values={{ err: health.change_stream_error }}
            components={{ code: <code /> }}
          />
        </p>
      )}
    </div>
  );
}

function Kpi({ l, v, icon: Icon, tone }) {
  const dot = tone === "emerald" ? "bg-emerald-100 text-emerald-700" :
              tone === "gold" ? "bg-gold/15 text-gold" :
              tone === "petroleum" ? "bg-petroleum text-gold" :
              "bg-slate-100 text-slate-600";
  return (
    <div className="kpi-card p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-slate-500">{l}</p>
          <p className="text-2xl font-display text-petroleum mt-1">{v ?? 0}</p>
        </div>
        <span className={`inline-flex h-9 w-9 rounded-full items-center justify-center ${dot}`}>
          <Icon size={14} />
        </span>
      </div>
    </div>
  );
}

function UsersTable({ t, role, rows, columns, onVerify, onSuspend, onDelete }) {
  return (
    <div className="kpi-card overflow-x-auto" data-testid={`admin-table-${role}s`}>
      <table className="w-full min-w-[900px]">
        <thead className="bg-slate-50 sticky top-0">
          <tr>
            <th className="text-left px-4 py-3 text-[10px] uppercase tracking-widest text-slate-500">{t("admin.col_user")}</th>
            <th className="text-left px-4 py-3 text-[10px] uppercase tracking-widest text-slate-500">{t("admin.col_score")}</th>
            {columns.map((c) => <th key={c.key} className="text-right px-4 py-3 text-[10px] uppercase tracking-widest text-slate-500">{c.label}</th>)}
            <th className="text-left px-4 py-3 text-[10px] uppercase tracking-widest text-slate-500">{t("admin.col_status")}</th>
            <th className="text-right px-4 py-3 text-[10px] uppercase tracking-widest text-slate-500">{t("admin.col_actions")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={5 + columns.length} className="px-4 py-6 text-center text-slate-400 text-sm">{t("admin.no_results")}</td></tr>
          ) : rows.map((u) => (
            <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50/50" data-testid={`admin-row-${u.id}`}>
              <td className="px-4 py-3">
                <p className="text-sm text-petroleum">{u.name}</p>
                <p className="text-xs text-slate-500">{u.email}</p>
              </td>
              <td className="px-4 py-3">
                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-semibold ${bandColor(u.band)}`}>
                  <Sparkles size={10} /> {u.score} · {u.band}
                </span>
              </td>
              {columns.map((c) => (
                <td key={c.key} className="px-4 py-3 text-sm text-slate-700 text-right tabular-nums">{u[c.key] ?? 0}</td>
              ))}
              <td className="px-4 py-3">
                <div className="flex flex-wrap gap-1">
                  {u.verified ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px]">{t("admin.status_verified")}</Badge> : <Badge variant="secondary" className="text-[10px]">{t("admin.status_pending")}</Badge>}
                  {u.suspended && <Badge className="bg-red-100 text-red-700 hover:bg-red-100 text-[10px]">{t("admin.status_suspended")}</Badge>}
                </div>
              </td>
              <td className="px-4 py-3 text-right">
                <div className="inline-flex gap-1 flex-wrap justify-end">
                  {!u.verified && (
                    <Button size="sm" variant="outline" onClick={() => onVerify(u.id)} data-testid={`button-verify-${u.id}`} className="h-7 text-xs">
                      <CheckCircle2 size={11} className="mr-1" /> {t("admin.verify")}
                    </Button>
                  )}
                  {u.role !== "admin" && (
                    <Button size="sm" variant="outline" onClick={() => onSuspend(u.id, u.suspended)} data-testid={`button-suspend-${u.id}`}
                            className={`h-7 text-xs ${u.suspended ? "text-emerald-700 border-emerald-200" : "text-amber-700 border-amber-200"}`}>
                      <Ban size={11} className="mr-1" /> {u.suspended ? t("admin.reactivate") : t("admin.suspend")}
                    </Button>
                  )}
                  {u.role !== "admin" && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="outline" data-testid={`button-delete-${u.id}`} className="h-7 text-xs text-red-600 border-red-200 hover:bg-red-50">
                          <Trash2 size={11} className="mr-1" /> {t("admin.delete")}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>{t("admin.delete_title", { name: u.name })}</AlertDialogTitle>
                          <AlertDialogDescription>
                            {t("admin.delete_desc")}
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                          <AlertDialogAction onClick={() => onDelete(u.id, u.email)} data-testid={`button-confirm-delete-${u.id}`} className="bg-red-600 hover:bg-red-700">
                            {t("admin.yes_delete")}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
